import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getDeclarations,
  submitDeclaration,
  getReleaseStatuses,
  getBonds,
  getTariffs,
  calculateDutyTax,
} from '../api';
import type { TariffResponse } from '../api';
import { carmKeys } from './queryKeys';
import type {
  DeclarationFilters,
  ReleaseFilters,
  BondFilters,
  TariffResource,
  TariffFilters,
  DutyCalculatorInput,
} from '../types';

export function useDeclarations(filters: DeclarationFilters, enabled = true, bearerToken?: string) {
  return useQuery({
    queryKey: [...carmKeys.declarations(filters), bearerToken ? 'auth' : 'noauth'],
    queryFn: () => getDeclarations(filters, bearerToken),
    enabled,
    retry: false,
  });
}

export function useSubmitDeclaration(bearerToken?: string) {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => submitDeclaration(body, bearerToken),
    onSuccess: () => toast.success('Declaration submitted'),
    onError: (error) => toast.error('Submission failed', {
      description: error instanceof Error ? error.message : 'Unknown error',
    }),
  });
}

export function useReleaseStatuses(filters: ReleaseFilters, enabled = true, bearerToken?: string) {
  return useQuery({
    queryKey: [...carmKeys.releases(filters), bearerToken ? 'auth' : 'noauth'],
    queryFn: () => getReleaseStatuses(filters, bearerToken),
    enabled,
    retry: false,
  });
}

export function useBonds(filters: BondFilters, enabled = true, bearerToken?: string) {
  return useQuery({
    queryKey: [...carmKeys.bonds(filters), bearerToken ? 'auth' : 'noauth'],
    queryFn: () => getBonds(filters, bearerToken),
    enabled,
    retry: false,
  });
}

export function useTariffs(resource: TariffResource, filters: TariffFilters, enabled = true) {
  return useQuery<TariffResponse>({
    queryKey: carmKeys.tariffs(resource, filters),
    queryFn: () => getTariffs(resource, filters),
    enabled,
    retry: false,
  });
}

export function useCalculateDutyTax() {
  return useMutation({
    mutationFn: (input: DutyCalculatorInput) => calculateDutyTax(input),
    onError: (error) => toast.error('Calculation failed', {
      description: error instanceof Error ? error.message : 'Unknown error',
    }),
  });
}
